import { useState, useEffect } from 'react'

/**
 * Custom hook for persisting form data to localStorage
 * Automatically saves form state and restores it on page refresh
 *
 * @param {string} formKey - Unique key for this form draft (e.g., 'clientForm', 'policyForm')
 * @param {object} initialData - Initial form data if no saved draft exists
 * @param {number} debounceMs - Debounce save time to avoid excessive writes (default: 1000ms)
 */
export function useFormDraft(formKey, initialData = {}, debounceMs = 1000) {
  const [data, setData] = useState(initialData)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const storageKey = `form_draft_${formKey}`

  // Load saved draft on mount
  useEffect(() => {
    const saved = localStorage.getItem(storageKey)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        setData(parsed)
      } catch (err) {
        console.warn(`Failed to parse saved draft for ${formKey}:`, err)
      }
    }
  }, [storageKey])

  // Save draft when data changes (with debounce)
  useEffect(() => {
    setHasUnsavedChanges(true)
    const timer = setTimeout(() => {
      localStorage.setItem(storageKey, JSON.stringify(data))
      setHasUnsavedChanges(false)
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [data, storageKey, debounceMs])

  // Clear saved draft
  const clearDraft = () => {
    localStorage.removeItem(storageKey)
    setData(initialData)
    setHasUnsavedChanges(false)
  }

  return {
    data,
    setData,
    hasUnsavedChanges,
    clearDraft,
  }
}
